import { useEffect, useMemo, useState } from 'react';
import usePlayer from '../contexts/usePlayer';
import useAuth from '../contexts/useAuth';
import type { ListData } from '../../../types';
import PlayIcon from './icons/PlayIcon';

const PlayButton = ({
  item,
  size = 40,
}: {
  item?: ListData;
  size?: number;
}) => {
  const { loading: authLoading } = useAuth();
  const {
    nowPlayingId,
    currentPlaylist,
    isPlaying,
    isLoading,
    isPlayRequested,
    error,
    play,
    playPlaylist,
    pause,
  } = usePlayer();

  const itemKey = useMemo(() => {
    if (!item) return 'current';
    // For playlists, the button represents the playlist id (not the chapter id).
    return `${item.type}:${item.id}`;
  }, [item]);

  const [optimisticLoadingKey, setOptimisticLoadingKey] = useState<
    string | null
  >(null);

  // Determine if this button represents the currently playing item
  const isCurrentItem = item
    ? item.type === 'playlist'
      ? currentPlaylist?.id === item.id
      : nowPlayingId === item.id
    : true; // No item means this controls the current player

  // Disable for failed content or while auth is loading
  const isFailed = item?.status === 'failed';
  const isDisabled = isFailed || authLoading;

  // Show loading state when: initial load (isPlayRequested) OR buffering mid-playback (isPlaying)
  const showLoading =
    isCurrentItem && isLoading && (isPlayRequested || isPlaying) && !isFailed;
  // Show playing state only if this is the current item and it's playing
  const showPlaying = isCurrentItem && isPlaying && !isLoading && !isFailed;

  const showOptimisticLoading =
    optimisticLoadingKey === itemKey &&
    !isFailed &&
    !isDisabled &&
    !showPlaying;

  // Hand off from optimistic UI to real player state as soon as it reflects the request.
  useEffect(() => {
    if (optimisticLoadingKey !== itemKey) return;
    if (isCurrentItem && (isLoading || isPlaying || !!error)) {
      setOptimisticLoadingKey(null);
    }
  }, [
    error,
    isCurrentItem,
    isLoading,
    isPlaying,
    itemKey,
    optimisticLoadingKey,
  ]);

  const handleClick = () => {
    if (isDisabled) return;
    if (isCurrentItem) {
      // Toggle play/pause for current item
      if (isLoading || showOptimisticLoading) {
        // Tapping spinner cancels loading and goes to paused state
        setOptimisticLoadingKey(null);
        pause();
      } else if (isPlaying) {
        pause();
      } else {
        // Immediately reflect the user's intent, even if player init is still happening.
        setOptimisticLoadingKey(itemKey);
        play();
      }
    } else if (item) {
      // Start playing this new item
      setOptimisticLoadingKey(itemKey);
      if (item.type === 'playlist') {
        playPlaylist(item);
      } else {
        play(item);
      }
    }
  };

  const className = isDisabled
    ? 'play-button disabled'
    : showLoading || showOptimisticLoading
      ? 'play-button loading'
      : showPlaying
        ? 'play-button playing'
        : 'play-button';

  return (
    <button
      className={className}
      onClick={(e) => {
        e.stopPropagation();
        handleClick();
      }}
      onTouchEnd={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleClick();
      }}
      disabled={isDisabled}
      style={{
        width: `${size}px`,
        height: `${size}px`,
      }}
    >
      {showLoading || showOptimisticLoading ? (
        <svg
          className="spinner"
          width={size * 0.5}
          height={size * 0.5}
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--background)"
          strokeWidth="3"
          strokeLinecap="round"
        >
          <path d="M12 2 A10 10 0 0 1 22 12" />
        </svg>
      ) : showPlaying ? (
        <svg
          width={size * 0.6}
          height={size * 0.6}
          viewBox="0 0 24 24"
          fill="var(--background)"
        >
          <path d="M6 4h4v16H6zM14 4h4v16h-4z" />
        </svg>
      ) : (
        <PlayIcon size={size * 0.7} color="var(--background)" />
      )}
    </button>
  );
};

export default PlayButton;
